import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "previewList",
    "submitButton",
    "submitText",
    "submitSpinner",
    "progress",
    "progressBar",
    "errors"
  ]

  connect() {
    this.objectUrls = []
  }

  disconnect() {
    this.revokeObjectUrls()
  }

  preview() {
    this.revokeObjectUrls()

    const files = Array.from(this.inputTarget.files || []).filter((file) =>
      file.type.startsWith("image/")
    )

    if (files.length === 0) {
      this.previewTarget.classList.add("hidden")
      this.previewListTarget.innerHTML = ""
      return
    }

    this.previewTarget.classList.remove("hidden")
    this.previewListTarget.innerHTML = ""

    files.slice(0, 4).forEach((file) => {
      const url = URL.createObjectURL(file)
      this.objectUrls.push(url)

      const item = document.createElement("div")
      item.className = "relative overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-900"
      item.innerHTML = `
        <img src="${url}" alt="" class="h-28 w-full object-cover sm:h-32">
        <div class="absolute inset-x-0 bottom-0 truncate bg-slate-950/60 px-2 py-1 text-xs text-white">${this.escapeHtml(file.name)}</div>
      `
      this.previewListTarget.appendChild(item)
    })

    if (files.length > 4) {
      const extra = document.createElement("div")
      extra.className = "flex h-28 items-center justify-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300 sm:h-32"
      const moreLabel = this.previewTarget.dataset.moreLabel || "+%{count} more"
      extra.textContent = moreLabel.replace("%{count}", String(files.length - 4))
      this.previewListTarget.appendChild(extra)
    }
  }

  submitStarted() {
    this.setSubmitting(true)
    this.clearErrors()
  }

  submitEnded(event) {
    this.setSubmitting(false)
    this.hideProgress()

    if (event.detail.success) {
      this.element.reset()
      this.previewTarget.classList.add("hidden")
      this.previewListTarget.innerHTML = ""
      this.revokeObjectUrls()
    }
  }

  uploadsStarted() {
    this.setSubmitting(true)
    this.showProgress(5)
  }

  uploadProgress(event) {
    this.showProgress(event.detail.progress || 0)
  }

  uploadsEnded() {
    this.showProgress(100)
  }

  setSubmitting(isSubmitting) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isSubmitting
      this.submitButtonTarget.classList.toggle("opacity-70", isSubmitting)
      this.submitButtonTarget.classList.toggle("cursor-wait", isSubmitting)
    }

    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.textContent = isSubmitting
        ? (this.submitTextTarget.dataset.postingLabel || "Posting…")
        : (this.submitTextTarget.dataset.postLabel || "Post")
    }

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.toggle("hidden", !isSubmitting)
    }
  }

  showProgress(progress) {
    if (!this.hasProgressTarget || !this.hasProgressBarTarget) return

    this.progressTarget.classList.remove("hidden")
    this.progressBarTarget.style.width = `${Math.max(5, Math.min(100, progress))}%`
  }

  hideProgress() {
    if (!this.hasProgressTarget || !this.hasProgressBarTarget) return

    this.progressTarget.classList.add("hidden")
    this.progressBarTarget.style.width = "0%"
  }

  clearErrors() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.innerHTML = ""
    }
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }

  escapeHtml(value) {
    return value.replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    }[character]))
  }
}
